import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Table, Pagination, Btn, Modal, Input, Select, StatusBadge, InfoAlert,
         PageHeader, StatsGrid, StatCard, FilterBar, SearchInput, FilterSelect, DetailRow } from '../components/UI';
import { IconGift, IconWallet, IconEdit, IconCheck, IconBan, IconTrash, IconUsers } from '../components/Icons';
import { fmt, PLANS } from '../utils/helpers';

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [statusF, setStatusF]   = useState('all');
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState({});
  const [busy, setBusy]         = useState(false);
  const toast = useToast();

  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getUsers({ page, limit:20, search, status:statusF });
      setUsers(r.users || []); setTotal(r.total || 0);
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  }, [page, search, statusF]);

  useEffect(() => { load(); }, [load]);

  const open  = (type, u) => { setSelected(u); setForm({}); setModal(type); };
  const close = () => { setModal(null); setSelected(null); setForm({}); };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast(msg || 'Done!','success'); close(); load(); }
    catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const columns = [
    { key:'name', label:'User', render: u => (
  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
    <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--accent-dim)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--accent)', flexShrink:0 }}>
      {u.name?.[0]?.toUpperCase()}
    </div>
    <div>
      <div style={{ fontWeight:500, fontSize:13 }}>{u.name}</div>
      <div style={{ fontSize:11, color:'var(--text3)' }}>{u.email}</div>
    </div>
  </div>
)},
    { key:'balance', label:'Balance', render: u => <span style={{ fontFamily:'Geist Mono,monospace', fontSize:12, fontWeight:600, color:'var(--green)' }}>{fmt.naira(u.wallet?.balance)}</span> },
    { key:'status',   label:'Status',   render: u => <StatusBadge status={u.is_active ? 'active':'inactive'} /> },
    { key:'verified', label:'Verified', render: u => <StatusBadge status={u.is_verified ? 'success':'pending'} /> },
    { key:'joined',   label:'Joined',   render: u => <span style={{ fontSize:11, color:'var(--text3)', whiteSpace:'nowrap' }}>{fmt.date(u.createdAt)}</span> },
    { key:'actions',  label:'Actions', noNav: true, render: u => (
      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
        <Btn size="xs" variant="subtle" icon={IconWallet} onClick={() => open('fund',u)}>Fund</Btn>
        <Btn size="xs" variant="subtle" icon={IconEdit}   onClick={() => open('adjust',u)}>Adjust</Btn>
        <Btn size="xs" variant="subtle" icon={IconGift}   onClick={() => open('grant',u)}>Plan</Btn>
        {!u.is_verified && <Btn size="xs" variant="subtle" icon={IconCheck} onClick={() => run(()=>api.verifyUser(u.id),'User verified')}>Verify</Btn>}
        <Btn size="xs" variant="ghost" icon={u.is_active ? IconBan : IconCheck}
          style={{ color: u.is_active ? 'var(--red)':'var(--green)' }}
          onClick={() => run(()=>api.toggleUserStatus(u.id,!u.is_active), u.is_active?'User disabled':'User enabled')}>
          {u.is_active ? 'Disable':'Enable'}
        </Btn>
        <Btn size="xs" variant="ghost" icon={IconTrash} style={{ color:'var(--red)' }}
          onClick={() => { if(window.confirm(`Delete ${u.name}?`)) run(()=>api.deleteUser(u.id),'User deleted'); }} />
      </div>
    )},
  ];

  const mobileCard = (u) => (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--accent-dim)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'var(--accent)', flexShrink:0 }}>{u.name?.[0]?.toUpperCase()}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</div>
          <div style={{ fontSize:11, color:'var(--text3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
        </div>
        <StatusBadge status={u.is_active?'active':'inactive'} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:13 }}>
        <span style={{ color:'var(--text3)' }}>Balance</span>
        <span style={{ fontFamily:'Geist Mono,monospace', fontWeight:600, color:'var(--green)' }}>{fmt.naira(u.wallet?.balance)}</span>
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        <Btn size="xs" variant="subtle" icon={IconWallet} onClick={() => open('fund',u)}>Fund</Btn>
        <Btn size="xs" variant="subtle" icon={IconEdit}   onClick={() => open('adjust',u)}>Adjust</Btn>
        <Btn size="xs" variant="subtle" icon={IconGift}   onClick={() => open('grant',u)}>Grant Plan</Btn>
        <Btn size="xs" variant="ghost" icon={u.is_active?IconBan:IconCheck} style={{ color:u.is_active?'var(--red)':'var(--green)' }}
          onClick={() => run(()=>api.toggleUserStatus(u.id,!u.is_active), u.is_active?'Disabled':'Enabled')}>
          {u.is_active?'Disable':'Enable'}
        </Btn>
      </div>
    </>
  );

  return (
    <div className="page-enter">
      <PageHeader title="Users" subtitle={`${total.toLocaleString()} registered users`} />

      <FilterBar>
        <SearchInput value={search} onChange={v=>{setSearch(v);setPage(1);}} placeholder="Search name or email…" />
        <FilterSelect value={statusF} onChange={v=>{setStatusF(v);setPage(1);}} options={[{value:'all',label:'All Status'},{value:'active',label:'Active'},{value:'inactive',label:'Inactive'}]} />
      </FilterBar>

      <Card style={{ padding:0, overflow:'hidden' }}>
        <Table columns={columns} rows={users} loading={loading} emptyText="No users found." mobileCard={mobileCard} onRowClick={u => navigate(`/users/${u.id}`)}  />
        <div style={{ borderTop:'1px solid var(--border)', padding:'0 14px' }}>
          <Pagination page={page} total={total} limit={20} onChange={setPage} />
        </div>
      </Card>

      {/* Fund Wallet Modal */}
      {modal==='fund' && (
        <Modal title={`Fund Wallet — ${selected?.name}`} onClose={close}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <InfoAlert type="info">Current balance: <b>{fmt.naira(selected?.wallet?.balance)}</b></InfoAlert>
            <Input label="Amount (₦)" type="number" value={form.amount||''} onChange={v=>setForm(f=>({...f,amount:v}))} required />
            <Input label="Bank Reference" value={form.reference||''} onChange={v=>setForm(f=>({...f,reference:v}))} placeholder="TRF202506120001" hint="Reference from the customer's bank transfer." required />
            <Input label="Description (optional)" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="success" icon={IconWallet} onClick={()=>run(()=>api.fundWallet(selected.id,{amount:parseFloat(form.amount),reference:form.reference,description:form.description}),'Wallet funded')} loading={busy} disabled={!form.amount||!form.reference}>Fund Wallet</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Adjust Wallet Modal */}
      {modal==='adjust' && (
        <Modal title={`Adjust Wallet — ${selected?.name}`} onClose={close}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <InfoAlert type="info">Current balance: <b>{fmt.naira(selected?.wallet?.balance)}</b></InfoAlert>
            <Select label="Type" value={form.adjustType||'topup'} onChange={v=>setForm(f=>({...f,adjustType:v}))}
              options={[{value:'topup',label:'+ Credit (add funds)'},{value:'debit',label:'− Debit (remove funds)'}]} />
            <Input label="Amount (₦)" type="number" value={form.amount||''} onChange={v=>setForm(f=>({...f,amount:v}))} required />
            <Input label="Reason" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} required />
            <InfoAlert type={form.adjustType==='debit'?'danger':'success'}>
              {form.adjustType==='debit' ? '⚠ Will deduct from wallet.' : '✓ Will add to wallet.'}
            </InfoAlert>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant={form.adjustType==='debit'?'danger':'success'} loading={busy} disabled={!form.amount||!form.description}
                onClick={()=>run(()=>api.adjustWallet(selected.id,{amount:parseFloat(form.amount),type:form.adjustType||'topup',description:form.description}),'Wallet adjusted')}>Apply</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Grant Plan Modal */}
      {modal==='grant' && (
        <Modal title={`Grant Plan — ${selected?.name}`} onClose={close}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Select label="Plan" value={form.plan||PLANS[0].value} onChange={v=>setForm(f=>({...f,plan:v}))}
              options={PLANS.map(p=>({value:p.value,label:`${p.label} — ${fmt.naira(p.price)}`}))} />
            <Input label="Device MAC Address" value={form.mac||''} onChange={v=>setForm(f=>({...f,mac:v}))} placeholder="xx:xx:xx:xx:xx:xx" hint="MAC address of the device to authorize." required />
            <Select label="Billing" value={form.grant_free||'false'} onChange={v=>setForm(f=>({...f,grant_free:v}))}
              options={[{value:'false',label:'Deduct from wallet'},{value:'true',label:'Free grant — no charge'}]} />
            <InfoAlert type={form.grant_free==='true'?'warning':'info'}>
              {form.grant_free==='true' ? '⚠ No charge will be applied.' : `💳 ${fmt.naira(PLANS.find(p=>p.value===(form.plan||PLANS[0].value))?.price)} will be deducted.`}
            </InfoAlert>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="success" icon={IconGift} loading={busy} disabled={!form.mac}
                onClick={()=>run(()=>api.grantSubscription(selected.id,{plan:form.plan||PLANS[0].value,mac_address:form.mac,grant_free:form.grant_free==='true'}),'Plan granted')}>Grant Plan</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}